import type { ChatMessage } from 'src/shared/components/SaraChat/types/chatMessage'
import { AssistantMessage } from 'src/shared/components/SaraChat/message_components/AssistantMessage'
import { ProductSelectionMessage } from 'src/shared/components/SaraChat/message_components/ProductSelectionMessage'
import type { ProductItem } from 'src/shared/components/SaraChat/message_components/ProductSelectionMessage/types'
import { StatusMessage } from 'src/shared/components/SaraChat/message_components/StatusMessage'
import { QuoteCrudMessage } from 'src/shared/components/SaraChat/message_components/QuoteCrudMessage'
import { MockupGenerationMessage } from 'src/shared/components/SaraChat/message_components/MockupGenerationMessage'
import { LogoFetchMessage } from 'src/shared/components/SaraChat/message_components/LogoFetchMessage'
import { FileAttachment } from 'src/shared/components/SaraChat/message_components/UserMessage/components/FileAttachment'
import type { ChatFile } from 'src/shared/components/SaraChat/message_components/UserMessage/types'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Image } from 'src/shared/components/ui'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { cn } from 'src/shared/components/ui/utils'
import { SaraAvatar } from './Chat/components/SaraAvatar'
import { OnlyVisibleToYouBadge } from './Chat/components/OnlyVisibleToYouBadge'
import { SARA_PREFILL_TEXT } from 'src/shared/components/GlobalChatWidget/components/saraPrefill'
import { formatMessageTimestamp } from '../utils/chatMessageDisplay'

export const parseSaraMessageContent = (message: ChatMessage) =>
  typeof message.content === 'string'
    ? JSON.parse(message.content || '{}')
    : message.content ?? {}

export {
  saraGroupHasCartEdits,
  saraGroupHasMockups,
  saraGroupHasProducts,
} from './saraQuickActions'

/** Whether SaraMessageBody would render anything for this message. */
export const hasSaraMessageBody = (message: ChatMessage) => {
  const content = parseSaraMessageContent(message)
  if (message.role === 'assistant') return Boolean(content.message)
  if (message.role === 'status') return Boolean(content)
  if (message.role === 'system')
    return [
      'product_search',
      'quote_crud',
      'mockup_generation',
      'logo_fetch',
    ].includes(content.tool_result)
  return false
}

export const isPendingMockupGeneration = (message: ChatMessage) => {
  if (message.role !== 'system') return false
  const content = parseSaraMessageContent(message)
  if (content.tool_result !== 'mockup_generation') return false
  if (content.pending === true) return true
  const results = content.data?.results ?? []
  return results.some((result: { pending?: boolean }) => result.pending)
}

export const isSuccessfulMockupGeneration = (message: ChatMessage) => {
  if (message.role !== 'system') return false
  const content = parseSaraMessageContent(message)
  if (content.tool_result !== 'mockup_generation' || content.skipped) {
    return false
  }
  if (isPendingMockupGeneration(message)) return false
  const results = content.data?.results ?? []
  return results.some(
    (result: { success?: boolean; mockup_urls?: string[] }) =>
      result.success && (result.mockup_urls?.length ?? 0) > 0
  )
}

export const SaraMessageHeader = ({
  createdAt,
  isPrivate,
}: {
  createdAt?: string | null
  isPrivate?: boolean
}) => (
  <Row className="flex-wrap items-center gap-x-2 gap-y-1">
    <span className="text-sm font-semibold text-foreground">Sara</span>
    {isPrivate && <OnlyVisibleToYouBadge />}
    {createdAt && (
      <span className="text-xs text-muted-foreground">
        {formatMessageTimestamp(createdAt)}
      </span>
    )}
  </Row>
)

/** Renders the content of a Sara message without the avatar/header shell. */
export const SaraMessageBody = ({
  message,
  quoteId,
  onOpenProductOverlay,
  selectedLogoUrl,
  onSelectLogo,
}: {
  message: ChatMessage
  quoteId: number
  onOpenProductOverlay?: (
    products: ProductItem[],
    quote_id: number,
    product_categories?: string[]
  ) => void
  selectedLogoUrl?: string | null
  onSelectLogo?: (url: string) => void
}) => {
  const content = parseSaraMessageContent(message)

  if (message.role === 'assistant' && content.message) {
    return <AssistantMessage content={content} quote_id={quoteId} i={0} />
  }

  if (message.role === 'status' && content) {
    return <StatusMessage content={content} i={0} />
  }

  if (message.role === 'system' && content.tool_result === 'product_search') {
    return (
      <ProductSelectionMessage
        quote_id={quoteId}
        result={{ ...content.result, search_type: content.search_type }}
        onOpenOverlay={onOpenProductOverlay}
      />
    )
  }

  if (message.role === 'system' && content.tool_result === 'quote_crud') {
    return <QuoteCrudMessage content={content} />
  }

  if (message.role === 'system' && content.tool_result === 'mockup_generation') {
    return <MockupGenerationMessage content={content} />
  }

  if (message.role === 'system' && content.tool_result === 'logo_fetch') {
    return (
      <LogoFetchMessage
        content={content}
        selectedLogoUrl={selectedLogoUrl}
        onSelectLogo={onSelectLogo}
      />
    )
  }

  return null
}

const SaraUserMessage = ({
  messageContent,
  files = [],
  createdAt,
  isPrivate,
}: {
  messageContent?: string
  files?: ChatFile[]
  createdAt?: string | null
  isPrivate?: boolean
}) => {
  const { t } = useLocalization()

  return (
    <Row
      className={cn(
        'gap-3 px-3 py-1.5',
        isPrivate && 'rounded-lg bg-stone-50/80'
      )}
    >
      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-stone-200 text-xs font-semibold text-stone-700">
        {t('You', 'Du').slice(0, 1)}
      </div>

      <Column className="min-w-0 flex-1 gap-1">
        <Row className="flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-sm font-semibold text-foreground">
            {t('You', 'Du')}
          </span>
          {isPrivate && <OnlyVisibleToYouBadge />}
          {createdAt && (
            <span className="text-xs text-muted-foreground">
              {formatMessageTimestamp(createdAt)}
            </span>
          )}
        </Row>

        <div
          className={cn(
            'rounded-xl px-3 py-2',
            isPrivate
              ? 'border border-dashed border-stone-200 bg-white/80'
              : 'border border-gray-200 bg-white shadow-sm'
          )}
        >
          {messageContent ? (
            <p className="text-sm text-foreground">
              {messageContent.startsWith(SARA_PREFILL_TEXT) ? (
                <>
                  <span className="inline-block rounded-md bg-violet-50 px-1.5 py-0.5 align-middle font-semibold leading-none text-violet-700 shadow-[inset_0_0_0_1px_rgba(124,58,237,0.15)]">
                    Sara
                  </span>{' '}
                  {messageContent.slice(SARA_PREFILL_TEXT.length)}
                </>
              ) : (
                messageContent
              )}
            </p>
          ) : null}
          {files.map((file) =>
            file.url && file.type?.startsWith('image/') ? (
              <Image
                key={file.url}
                src={file.url}
                alt={file.name}
                className="mt-1 h-16 w-16 rounded-md border border-gray-200 bg-white object-contain"
              />
            ) : (
              <FileAttachment
                key={file.name}
                name={file.name}
                size={file.size}
                type={file.type}
              />
            )
          )}
        </div>
      </Column>
    </Row>
  )
}

export const saraMessageShellClass = (isPrivate: boolean) =>
  cn(
    'group gap-3 px-3 py-1.5 transition-colors hover:bg-muted/30',
    isPrivate && 'rounded-lg bg-stone-50/80 hover:bg-stone-50'
  )

export const RequestSaraMessage = ({
  message,
  quoteId,
  isPrivate = false,
  onOpenProductOverlay,
}: {
  message: ChatMessage
  quoteId: number
  isPrivate?: boolean
  onOpenProductOverlay?: (
    products: ProductItem[],
    quote_id: number,
    product_categories?: string[]
  ) => void
}) => {
  const content = parseSaraMessageContent(message)

  if (message.role === 'user' && (content.message || content.files?.length)) {
    return (
      <SaraUserMessage
        messageContent={content.message}
        files={content.files}
        createdAt={message.created_at}
        isPrivate={isPrivate}
      />
    )
  }

  if (!hasSaraMessageBody(message)) return null

  return (
    <Row className={saraMessageShellClass(isPrivate)}>
      <SaraAvatar />

      <Column className="min-w-0 flex-1 gap-0.5">
        <SaraMessageHeader
          createdAt={message.created_at}
          isPrivate={isPrivate}
        />
        <SaraMessageBody
          message={message}
          quoteId={quoteId}
          onOpenProductOverlay={onOpenProductOverlay}
        />
      </Column>
    </Row>
  )
}
